import type { Guide } from '@/lib/guides/types/guide'
import type { PageViewMode } from '@/components/pages/home'
import { computeGuideGutters } from '@/lib/guides/columns-rows-fit'

/**
 * Collects guides, dropping any whose (type, position rounded to 1/1000pt) was already added —
 * the first guide at a position wins. Non-finite positions are skipped.
 */
function createGuideCollector() {
  const guides: Guide[] = []
  const seen = new Set<string>()

  const add = (type: Guide['type'], position: number, id: string) => {
    if (!Number.isFinite(position)) return
    const key = `${type}:${Math.round(position * 1000) / 1000}`

    if (seen.has(key)) return
    seen.add(key)
    guides.push({ id, type, position })
  }

  return { guides, add }
}

/** Column/row gutter guides inside each visible page's margins (snap targets, `auto-` ids). */
export function computeGeneratedGridSnapGuides({
  pageTrimWidth,
  bleedMargin,
  trimHeight,
  marginTop,
  marginBottom,
  marginInside,
  marginOutside,
  showLeftPageGuides,
  showRightPageGuides,
  columns,
  rows,
  colGutter,
  rowGutter,
  pageViewMode,
}: {
  pageTrimWidth: number
  bleedMargin: number
  trimHeight: number
  marginTop: number
  marginBottom: number
  marginInside: number
  marginOutside: number
  showLeftPageGuides: boolean
  showRightPageGuides: boolean
  columns: number
  rows: number
  colGutter: number
  rowGutter: number
  pageViewMode: PageViewMode
}): Guide[] {
  const innerTop = bleedMargin + marginTop
  const innerHeight = trimHeight - marginTop - marginBottom

  const ptw = pageTrimWidth

  if (ptw <= 0 || innerHeight <= 0) {
    return []
  }

  const pageConfigs = [
    {
      key: 'left',
      innerLeft: bleedMargin + marginOutside,
      innerWidth: ptw - marginOutside - marginInside,
      guidesEnabled: showLeftPageGuides && pageViewMode !== 'single-first',
    },
    {
      key: 'right',
      innerLeft: bleedMargin + ptw + marginInside,
      innerWidth: ptw - marginInside - marginOutside,
      guidesEnabled: showRightPageGuides && pageViewMode !== 'single-last',
    },
  ] as const

  const { guides: next, add } = createGuideCollector()

  const addGuide = (type: Guide['type'], position: number, idPart: string) =>
    add(type, position, `auto-${idPart}`)

  for (const page of pageConfigs) {
    if (!page.guidesEnabled) continue

    const { innerLeft, innerWidth } = page

    if (innerWidth <= 0) continue

    for (const gutter of computeGuideGutters(columns, colGutter, innerWidth)) {
      addGuide('vertical', innerLeft + gutter.start, `${page.key}-col-start-${gutter.index}`)
      addGuide('vertical', innerLeft + gutter.end, `${page.key}-col-end-${gutter.index}`)
    }

    for (const gutter of computeGuideGutters(rows, rowGutter, innerHeight)) {
      addGuide('horizontal', innerTop + gutter.start, `${page.key}-row-start-${gutter.index}`)
      addGuide('horizontal', innerTop + gutter.end, `${page.key}-row-end-${gutter.index}`)
    }
  }

  return next
}

/** Page-margin guides (top/bottom, plus inside/outside per visible page) as snap targets. */
export function computeMarginSnapGuides({
  trimWidth,
  pageTrimWidth,
  trimHeight,
  bleedMargin,
  marginTop,
  marginBottom,
  marginInside,
  marginOutside,
  pageViewMode,
}: {
  trimWidth: number
  pageTrimWidth: number
  trimHeight: number
  bleedMargin: number
  marginTop: number
  marginBottom: number
  marginInside: number
  marginOutside: number
  pageViewMode: PageViewMode
}): Guide[] {
  if (trimWidth <= 0 || trimHeight <= 0) return []

  const { guides: mg, add } = createGuideCollector()

  if (marginTop > 0) add('horizontal', bleedMargin + marginTop, 'margin-top')
  if (marginBottom > 0) add('horizontal', bleedMargin + trimHeight - marginBottom, 'margin-bottom')

  const ptw = pageTrimWidth

  const pages = [
    {
      key: 'left',
      trimLeft: bleedMargin,
      insideOnLeft: false,
      visible: pageViewMode !== 'single-first',
    },
    {
      key: 'right',
      trimLeft: bleedMargin + ptw,
      insideOnLeft: true,
      visible: pageViewMode !== 'single-last',
    },
  ] as const

  for (const p of pages) {
    if (!p.visible) continue
    const leftInset = p.insideOnLeft ? marginInside : marginOutside
    const rightInset = p.insideOnLeft ? marginOutside : marginInside

    if (leftInset > 0) add('vertical', p.trimLeft + leftInset, `margin-${p.key}-left`)
    if (rightInset > 0) add('vertical', p.trimLeft + ptw - rightInset, `margin-${p.key}-right`)
  }

  return mg
}
